import {
  useState,
  useRef,
  useMemo,
  useEffect,
  RefObject,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react';
import { Button, Typography, Tooltip } from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolume, faVolumeSlash } from '@fortawesome/pro-regular-svg-icons';
import { Modal } from '@client/new/components/Modal/Modal';
import { PrimaryButton, SecondaryButton } from '@client/new/components/button';
import { getVideoTimeFormat } from '../videos.helpers';
import { VideoPanel } from './VideoPanel';
import { VideosViewModel } from '../videos.viewModel';
import toast from 'react-hot-toast';
import classNames from 'classnames';
import {
  ValidationMessage,
  getMediaFileName,
  validateVideo,
} from './editVideo.helpers';
import { allowedVideoTypes, videoLengthLimitSec } from '../videos.constants';
import { EditVideoName } from './EditVideoName';
import { EditVideoStatus } from './EditVideoStatus';
import MediaThumbnailSelect from './MediaThumbnailSelect';
import { EditParams } from '../videos.model';

export type EditVideoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  media: File;
  onUpload: (p: { file: File; editParams?: EditParams }) => void;
  onCancel: () => void;
  onSaveVideo: (p: { file: File; editParams?: EditParams }) => void;
  onSaveThumbnail: (time: number) => void;
  onSetStep: (step: EditStep) => void;
  editParams?: EditParams;
} & Pick<VideosViewModel, 'step'>;

type EditStep = 'trim' | 'thumbnail';

const getModalTitle = (step: EditStep) => {
  switch (step) {
    case 'trim':
      return 'Edit Video';
    case 'thumbnail':
      return 'Change thumbnail';
  }
};

export function EditVideoModal(p: EditVideoModalProps) {
  const upload = () => {
    p.onUpload({ file: p.media, editParams: p.editParams });
    p.onClose();
  };

  return (
    <Modal
      title={getModalTitle(p.step)}
      height="auto"
      sx={{
        maxWidth: { sm: '100vw', md: 850, lg: 850 },
        maxHeight: { sm: '100vh', md: '100vh', lg: '100vh' },
        width: { sm: '100vw', md: '850px !important', lg: '850px !important' },
      }}
      isOpen={p.isOpen}
      onClose={p.onClose}
      disableBackdropClick={true}
      titleVariant="h4"
    >
      <>
        {p.step === 'trim' ? (
          <EditContent
            media={p.media}
            onSave={p.onSaveVideo}
            onClose={p.onClose}
            onCancel={p.onCancel}
          />
        ) : (
          <>
            <MediaThumbnailSelect
              media={p.media}
              onChange={p.onSaveThumbnail}
              width={802}
              height={79}
              ratio={9 / 16}
              startTime={p.editParams?.startTime}
              endTime={p.editParams?.endTime}
            />
            <div className="flex justify-end">
              <div className="flex gap-4">
                <PrimaryButton onClick={upload}>Save</PrimaryButton>
              </div>
            </div>
          </>
        )}
      </>
    </Modal>
  );
}

type EditContentProps = {
  media: File;
  onSave: (p: {
    file: File;
    editParams?: EditParams;
    changeStep?: boolean;
  }) => void;
  onClose: () => void;
  onCancel: () => void;
};
function EditContent(p: EditContentProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const currentDisplyRef = useRef<HTMLSpanElement>(null);
  const timeEndRef = useRef<number>(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const initialfileName = getMediaFileName(p.media.name);
  const [isVideoLoaded, setLoadedVideo] = useState<boolean>(false);
  const [validationMessage, setValidationMessage] =
    useState<ValidationMessage>();
  const [duration, setDuration] = useState(0);
  const [fileName, setFileName] = useState(initialfileName);
  const [timeRange, setTimeRange] = useState<number[]>([0, 100]);
  const [keepAudio, setKeepAudio] = useState(true);
  const [limitTimeRangeDistance, setLimitTimeRangeDistance] =
    useState<number>(100);
  const [isPlaying, setIsPlaying] = useState(false);

  const videoPanelWidth = 700;

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener('timeupdate', (e) => updateProgress(e));
      videoRef.current.addEventListener('loadedmetadata', function () {
        if (videoRef.current) {
          setDuration(videoRef.current.duration);
          timeEndRef.current = videoRef.current.duration;

          if (videoRef.current.duration) {
            const limit =
              (videoLengthLimitSec / videoRef.current.duration) * 100;
            setLimitTimeRangeDistance(limit);
          }
          const message = validateVideo({
            duration: videoRef.current.duration,
            fileSize: p.media.size,
            videoHeight: videoRef.current.videoHeight,
            videoWidth: videoRef.current.videoWidth,
            videoType: videoRef.current.duration < 90 ? 'short' : 'long',
          });
          setValidationMessage(message);
          setLoadedVideo(true);
        }
      });
    }
  }, [p.media]);

  const handleSave = async () => {
    const editParams: EditParams = {};
    if (!keepAudio) editParams.removeAudio = true;
    if (timeRange[0] !== 0) {
      editParams.startTime = (duration * timeRange[0]) / 100;
    }
    if (timeRange[1] !== 100) {
      editParams.endTime = (duration * timeRange[1]) / 100;
    }

    const videoExtension = p.media.name.split('.').pop();
    const file =
      fileName !== p.media.name
        ? new File([p.media], `${fileName}.${videoExtension}`, {
            type: p.media.type,
          })
        : p.media;
    p.onSave({ file, editParams });
  };

  const videoSrc = useMemo(
    () => (p.media ? URL.createObjectURL(p.media) : ''),
    [p.media],
  );

  const restoreVideo = () => {
    setTimeRange([0, 100]);
    const endRange = videoRef?.current
      ? (videoLengthLimitSec / videoRef.current.duration) * 100
      : 100;
    setTimeRange([0, endRange]);
    setKeepAudio(true);
    setFileName(initialfileName);
  };

  const updateProgress = (e: Event) => {
    const event = e as any;
    if (event.target) {
      const time = event.target.currentTime;
      if (timeEndRef.current && time >= timeEndRef.current) {
        videoRef.current?.pause();
        setIsPlaying(false);
      }
      if (time <= event.target.duration) {
        if (currentDisplyRef?.current)
          currentDisplyRef.current.innerText = getVideoTimeFormat(time);
        const progressPosition =
          videoPanelWidth * (time / event.target.duration);
        if (progressRef?.current) {
          progressRef.current.style.left = `${progressPosition}px`;
        }
      }
    }
  };

  const addVideo = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files && ev.target.files.length === 0) {
      toast.error('Select file');
      return;
    }
    ev.target.files && p.onSave({ file: ev.target.files[0] });
  };

  const hasError = validationMessage?.type === 'error';

  return (
    <div>
      <EditVideoStatus validationMessage={validationMessage} />
      {!hasError && isVideoLoaded && (
        <EditVideoName
          fileName={fileName}
          setFileName={setFileName}
          initialName={initialfileName}
        />
      )}
      <div className="flex justify-center bg-black">
        <video
          controls={false}
          muted={!keepAudio}
          ref={videoRef}
          src={videoSrc}
          className="w-[500px] h-[410px]"
        />
      </div>
      <VideoTrim
        progressRef={progressRef}
        videoSrc={videoSrc}
        videoRef={videoRef}
        currentDisplyRef={currentDisplyRef}
        limitTimeRangeDistance={limitTimeRangeDistance}
        duration={duration}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        hideTrimFrame
        keepAudio={keepAudio}
        setKeepAudio={setKeepAudio}
        timeEndRef={timeEndRef}
        hasError={hasError}
        isVideoLoaded={isVideoLoaded}
      />
      <VideoEditCTA
        hasValidationMessage={hasError}
        restoreVideo={restoreVideo}
        inputRef={inputRef}
        addVideo={addVideo}
        handleSave={handleSave}
      />
    </div>
  );
}

type VideoEditCTAProps = {
  hasValidationMessage: boolean;
  restoreVideo: () => void;
  inputRef: RefObject<HTMLInputElement>;
  addVideo: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  handleSave: () => void;
};

const VideoEditCTA = ({
  hasValidationMessage,
  restoreVideo,
  inputRef,
  addVideo,
  handleSave,
}: VideoEditCTAProps) => {
  return (
    <div className="flex pt-[24px] justify-end">
      {!hasValidationMessage && (
        <SecondaryButton
          style={{ backgroundColor: 'white', color: 'black' }}
          onClick={restoreVideo}
        >
          Restore changes
        </SecondaryButton>
      )}
      <div
        className={classNames('flex gap-4', {
          'ml-auto': !hasValidationMessage,
        })}
      >
        {hasValidationMessage ? (
          <>
            <PrimaryButton onClick={() => inputRef.current?.click()}>
              Choose new video
            </PrimaryButton>

            <input
              type="file"
              accept={allowedVideoTypes.join(', ')}
              ref={inputRef}
              onChange={addVideo}
              hidden
            />
          </>
        ) : (
          <PrimaryButton onClick={handleSave}>Next</PrimaryButton>
        )}
      </div>
    </div>
  );
};

type VideoTrimProps = {
  videoSrc: string;
  videoRef: RefObject<HTMLVideoElement>;
  currentDisplyRef: RefObject<HTMLSpanElement>;
  limitTimeRangeDistance: number;
  duration: number;
  isPlaying: boolean;
  setIsPlaying: Dispatch<SetStateAction<boolean>>;
  timeRange: number[];
  setTimeRange: Dispatch<SetStateAction<number[]>>;
  hideTrimFrame: boolean;
  keepAudio: boolean;
  setKeepAudio: Dispatch<SetStateAction<boolean>>;
  progressRef: RefObject<HTMLDivElement>;
  timeEndRef: MutableRefObject<number>;
  hasError: boolean;
  isVideoLoaded: boolean;
};

function VideoTrim({
  videoSrc,
  videoRef,
  currentDisplyRef,
  limitTimeRangeDistance,
  duration,
  isPlaying,
  setIsPlaying,
  timeRange,
  setTimeRange,
  hideTrimFrame,
  keepAudio,
  setKeepAudio,
  progressRef,
  timeEndRef,
  hasError,
  isVideoLoaded,
}: VideoTrimProps) {
  if (hasError || !isVideoLoaded) {
    return null;
  }

  const videoPanelWidth = 700;

  const onTimeRangeChange = (value: [number, number]) => {
    setTimeRange(value);
    timeEndRef.current = (duration * value[1]) / 100;
  };

  const videoratio = 16 / 9;
  return (
    <div>
      <Typography className="py-6">
        {hideTrimFrame
          ? 'Trim video is currently not available for long videos'
          : 'Trim the video to meet the expected length.'}
      </Typography>
      <div className="flex justify-center items-center relative mb-5">
        <div className="flex items-center">
          <div className="text-center">
            <span ref={currentDisplyRef} className="min-w-5 text-center">
              {getVideoTimeFormat(0)}
            </span>
            /{getVideoTimeFormat(duration)}
          </div>
        </div>
        <div className="absolute right-0 flex items-center">
          <Tooltip
            placement="top-end"
            title="Remove audio. This action is irreversible once the video is uploaded."
          >
            <Button
              variant="text"
              onClick={() => setKeepAudio((state) => !state)}
            >
              <FontAwesomeIcon icon={keepAudio ? faVolume : faVolumeSlash} />
            </Button>
          </Tooltip>
        </div>
      </div>
      <VideoPanel
        timeRange={timeRange as [number, number]}
        onChange={onTimeRangeChange}
        videoRef={videoRef}
        height={79}
        width={videoPanelWidth}
        progressRef={progressRef}
        duration={duration}
        src={videoSrc}
        ratio={videoratio}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        limitTimeRangeDistance={limitTimeRangeDistance}
        isMaxRangeLimit={false}
        hideTrimFrame={hideTrimFrame}
      />
    </div>
  );
}
